import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Search, Calendar, Tag, ArrowRight, Newspaper } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function BlogList() {
  const [articoli, setArticoli] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [categoriaSel, setCategoriaSel] = useState("");

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (categoriaSel) params.set("categoria", categoriaSel);
    params.set("limit", "24");
    axios
      .get(`${API}/api/blog?${params.toString()}`)
      .then((r) => setArticoli(r.data.items || []))
      .catch(() => setArticoli([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [categoriaSel]);

  const categorie = Array.from(
    new Set(articoli.map((a) => a.categoria).filter(Boolean))
  );

  // JSON-LD BlogPosting collection
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "News & Blog A.C.D.T.",
    url: "https://www.assoacdt.com/blog",
    description:
      "News, aggiornamenti normativi e approfondimenti per Consulenti Digitali certificati A.C.D.T.",
    blogPost: articoli.slice(0, 10).map((a) => ({
      "@type": "BlogPosting",
      headline: a.titolo,
      description: a.excerpt,
      datePublished: a.pubblicato_at,
      url: `https://www.assoacdt.com/blog/${a.slug}`,
    })),
  };

  return (
    <div className="relative min-h-screen bg-white">
      <SEO
        title="News & Blog A.C.D.T. — Aggiornamenti sui Consulenti Digitali"
        description="News, normativa (Legge 4/2013), SPID, Firma Digitale, CFU/CFP: gli approfondimenti ufficiali dell'associazione A.C.D.T. per i Consulenti Digitali certificati."
        jsonLd={jsonLd}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-16 md:pt-20 pb-10 md:pb-14 bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a] text-white overflow-hidden">
        <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-[#f59e0b]/20 blur-3xl pointer-events-none" />
        <div className="max-w-6xl mx-auto px-5 md:px-8 relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#f59e0b]/20 text-[#fbbf24] text-[11px] font-extrabold uppercase tracking-[0.2em] mb-4">
            <Newspaper className="h-3.5 w-3.5" /> News & Blog
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight max-w-3xl">
            News & Aggiornamenti A.C.D.T.
          </h1>
          <p className="mt-4 text-slate-200 text-base md:text-lg max-w-2xl">
            Normativa, formazione continua, esperienze dei Consulenti Digitali
            certificati e novità dal Registro Nazionale.
          </p>

          <div className="mt-8 flex flex-col md:flex-row gap-3 md:items-center max-w-2xl">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                data-testid="blog-search-input"
                placeholder="Cerca negli articoli…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                className="pl-10 bg-white/95 text-[#0a1f55]"
              />
            </div>
            <Button
              data-testid="blog-search-btn"
              onClick={load}
              className="bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1f55] font-bold"
            >
              Cerca
            </Button>
          </div>

          {categorie.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-2">
              <button
                onClick={() => setCategoriaSel("")}
                data-testid="blog-cat-all"
                className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                  categoriaSel === ""
                    ? "bg-[#f59e0b] text-[#0a1f55] border-transparent"
                    : "bg-white/10 text-white border-white/20 hover:bg-white/20"
                }`}
              >
                Tutti
              </button>
              {categorie.map((c) => (
                <button
                  key={c}
                  onClick={() => setCategoriaSel(c)}
                  data-testid={`blog-cat-${c}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                    categoriaSel === c
                      ? "bg-[#f59e0b] text-[#0a1f55] border-transparent"
                      : "bg-white/10 text-white border-white/20 hover:bg-white/20"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lista */}
      <section className="relative py-14 md:py-20">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          {loading ? (
            <div className="text-center text-slate-500 py-16">Caricamento…</div>
          ) : articoli.length === 0 ? (
            <div
              data-testid="blog-empty"
              className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-500"
            >
              Nessun articolo trovato con i filtri attuali.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-list">
              {articoli.map((a) => (
                <ArticoloCard key={a.id} articolo={a} />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function ArticoloCard({ articolo }) {
  return (
    <Link
      to={`/blog/${articolo.slug}`}
      data-testid={`blog-card-${articolo.slug}`}
      className="group flex flex-col rounded-2xl border border-slate-200 bg-white overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all"
    >
      <div className="aspect-[16/9] bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a] relative overflow-hidden">
        {articolo.cover_url ? (
          <img
            src={articolo.cover_url}
            alt={articolo.titolo}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-white/40 text-6xl font-black">
            A.C.D.T.
          </div>
        )}
        {articolo.categoria && (
          <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-[#f59e0b] text-[#0a1f55] text-[10px] font-extrabold uppercase tracking-widest">
            {articolo.categoria}
          </span>
        )}
      </div>
      <div className="flex-1 p-5 flex flex-col">
        <div className="flex items-center gap-3 text-[11px] text-slate-500 mb-2">
          <span className="inline-flex items-center gap-1">
            <Calendar className="h-3 w-3" /> {formatDate(articolo.pubblicato_at)}
          </span>
          {articolo.autore && <span>• {articolo.autore}</span>}
        </div>
        <h3 className="text-lg font-extrabold text-[#0a1f55] leading-snug group-hover:text-[#102a6b]">
          {articolo.titolo}
        </h3>
        {articolo.excerpt && (
          <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed line-clamp-3 flex-1">
            {articolo.excerpt}
          </p>
        )}
        <div className="mt-4 inline-flex items-center gap-1.5 text-[#f59e0b] text-sm font-bold group-hover:gap-2 transition-all">
          Leggi l&apos;articolo <ArrowRight className="h-4 w-4" />
        </div>
        {articolo.tags && articolo.tags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5 border-t border-slate-100 pt-3">
            {articolo.tags.slice(0, 3).map((t) => (
              <span
                key={t}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-[10.5px] font-semibold"
              >
                <Tag className="h-2.5 w-2.5" /> {t}
              </span>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}
